import { useRef, useEffect } from 'react'
import { Control, FieldValues, Path, useController } from 'react-hook-form'
import { BsChevronDown } from 'react-icons/bs'

import {
  CommonEditorProps,
  EditorProps,
  useEditorCore,
  EditorToolbar,
} from 'components/Editor/EditorCore'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import 'components/Editor/Editor.css'

const EMAIL_VARIABLES = [
  { value: '{FIRST_NAME}', label: 'First Name' },
  { value: '{LAST_NAME}', label: 'Last Name' },
  { value: '{EMAIL}', label: 'Email' },
  { value: '{COMPANY}', label: 'Company' },
] as const

interface EmailBodyEditorProps<T extends FieldValues> {
  control: Control<T>
  name: Path<T>
  label?: string
  placeholder?: string
}

export function EmailBodyEditor<T extends FieldValues>(
  props: EmailBodyEditorProps<T> &
    Omit<CommonEditorProps, 'additionalToolbarButtons'>
) {
  const {
    control,
    name,
    label = 'Body',
    placeholder = 'Write your email content here...',
    ...rest
  } = props

  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({ control, name })

  const editorProps: EditorProps & CommonEditorProps = {
    ...rest,
    value: value || '',
    onChange,
    placeholder,
  }

  const { uniqueId, quillEditor } = useEditorCore(editorProps)

  // Track cursor position continuously via selection-change and text-change events
  const lastCursorPosition = useRef<number>(0)

  useEffect(() => {
    if (!quillEditor) return

    const updateCursorPosition = () => {
      const selection = quillEditor.getSelection()
      if (selection) {
        lastCursorPosition.current = selection.index + selection.length
      }
    }

    const handleSelectionChange = (
      range: { index: number; length: number } | null
    ) => {
      if (range) {
        lastCursorPosition.current = range.index + range.length
      }
    }

    const handleTextChange = () => {
      // Update cursor position after text changes (typing)
      updateCursorPosition()
    }

    quillEditor.on('selection-change', handleSelectionChange)
    quillEditor.on('text-change', handleTextChange)

    return () => {
      quillEditor.off('selection-change', handleSelectionChange)
      quillEditor.off('text-change', handleTextChange)
    }
  }, [quillEditor])

  // Track if we just inserted a variable (to focus after dropdown closes)
  const pendingFocusPosition = useRef<number | null>(null)

  const insertVariable = (variable: string) => {
    if (!quillEditor) return

    // Ensure position is within bounds
    const maxPosition = Math.max(0, quillEditor.getLength() - 1)
    const position = Math.min(lastCursorPosition.current, maxPosition)

    // Insert the variable text
    quillEditor.insertText(position, variable)

    // Store position to focus after dropdown closes
    const newPosition = position + variable.length
    lastCursorPosition.current = newPosition
    pendingFocusPosition.current = newPosition
  }

  const handleDropdownOpenChange = (open: boolean) => {
    // When dropdown closes, focus editor if we have a pending position
    if (!open && pendingFocusPosition.current !== null && quillEditor) {
      const pos = pendingFocusPosition.current
      pendingFocusPosition.current = null
      // Delay to let dropdown fully release focus
      setTimeout(() => {
        quillEditor.focus()
        quillEditor.setSelection(pos, 0)
      }, 50)
    }
  }

  const variableDropdownButton = (
    <DropdownMenu onOpenChange={handleDropdownOpenChange} modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title="Insert Variable"
          style={{
            display: 'inline-flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '4px',
            height: '24px',
            padding: '0 8px',
            border: 'none',
            background: 'white',
            color: '#444',
            fontSize: '12px',
            fontWeight: 500,
            cursor: 'pointer',
            borderRadius: '3px',
          }}
        >
          <span>Variables</span>
          <BsChevronDown style={{ width: 12, height: 12, flexShrink: 0 }} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {EMAIL_VARIABLES.map((variable) => (
          <DropdownMenuItem
            key={variable.value}
            onClick={() => insertVariable(variable.value)}
          >
            {variable.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )

  return (
    <div className="space-y-1">
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      <div
        className={`border rounded-md bg-white ${error ? 'border-red-500' : 'border-gray-300'}`}
      >
        <div id={`editor-${uniqueId}`} className="min-h-[120px] bg-white"></div>
        <EditorToolbar
          uniqueId={uniqueId}
          additionalToolbarButtons={[variableDropdownButton]}
        />
      </div>
      {error && <p className="text-xs text-red-500 mt-1">{error.message}</p>}
      <p className="text-xs text-gray-500">
        Use variables like {'{FIRST_NAME}'} to personalize your emails.
      </p>
    </div>
  )
}
